import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuthAppContext } from '../../context/useAuthAppContext';
import { useLogger } from '../../context/useLogger';
import { KeyAuthService } from '../../services/KeyAuthService';
import PageLoadingComponent from '../../components/PageLoading';
import { AppRoutes } from '../../types/appRoutes';
import Message from 'carbon-react/esm/components/message';
import Typography from 'carbon-react/esm/components/typography';
import Box from 'carbon-react/esm/components/box';
import Button from 'carbon-react/esm/components/button/__next__';
import { ENV } from '../../config/env';
import { resolveRedirectFromStateConnect } from '../../utils/stateConnectParser';
import { accessTokenKeySchema } from '../../types/schemas/keyAuth.schema';

const KeyAuth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const logger = useLogger();
  const { authenticateWithKey } = useAuthAppContext();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const authenticateWithKeyFromUrl = async () => {
      const key = searchParams.get('accessTokenKey');

      if (!key) {
        logger.warn('Key authentication failed: no key parameter in URL');

        if (ENV.DEBUG_MODE) {
          setError("Aucune clé d'authentification n'a été fournie dans l'URL");
          setIsLoading(false);
          return;
        }

        void navigate(AppRoutes.routeLogout, { replace: true });
        return;
      }

      const keyValidation = accessTokenKeySchema.safeParse(key);
      if (!keyValidation.success) {
        logger.warn('Key authentication failed: malformed key parameter', {
          reason: keyValidation.error.issues[0]?.message,
        });
        setError("La clé d'authentification fournie est invalide");
        setIsLoading(false);
        return;
      }

      logger.info('🔑 Starting key authentication process');

      try {
        const keyAuthService = new KeyAuthService(logger);
        const accessToken = await keyAuthService.exchangeKeyForToken(keyValidation.data);

        logger.info('✅ Access token received, fetching user information');
        const user = await keyAuthService.getUserInfo(accessToken);

        logger.info('✅ User information received, storing authentication');
        authenticateWithKey(accessToken, user);

        const stateConnect = searchParams.get('stateConnect');
        const routeRedirect = resolveRedirectFromStateConnect(stateConnect, logger);

        logger.info('🔄 Redirecting after authentication');
        await navigate(routeRedirect === 'DsnList' ? AppRoutes.routeDsnList : AppRoutes.routeHome, {
          replace: true,
        });
      } catch (err) {
        if (cancelled) {
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : "Une erreur inattendue s'est produite";
        logger.error('Key authentication failed:', err);
        setError(errorMessage);
        setIsLoading(false);
      }
    };

    void authenticateWithKeyFromUrl();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, logger, authenticateWithKey]);

  if (isLoading) {
    return <PageLoadingComponent />;
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        padding="var(--spacing400)"
      >
        <Box maxWidth="600px" width="100%">
          <Message variant="error" title="Erreur d'authentification" mb={3}>
            {error}
          </Message>

          <Typography variant="p" mb={3}>
            L&apos;authentification par clé a échoué. Veuillez vérifier que votre clé est valide et
            n&apos;a pas expiré.
          </Typography>

          <Box display="flex" gap={2} justifyContent="center">
            <Link to={AppRoutes.routeLogin} style={{ textDecoration: 'none' }}>
              <Button variantType="primary">Se connecter</Button>
            </Link>

            <Button onClick={() => window.location.reload()} variantType="secondary">
              Réessayer
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};

export default KeyAuth;
